'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'writer' | 'editor' | 'admin' | 'owner';
  requiredPermission?: Permission; // Alternative to role-based access
}

export default function ProtectedRoute({
  children,
  requiredRole = 'admin',
  requiredPermission
}: ProtectedRouteProps) {
  const { user, userData, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const router = useRouter();

  // Check if user has required permission
  const hasRequiredPermission = requiredPermission ? hasPermission(requiredPermission) : true;

  useEffect(() => {
    if (!loading) {
      if (!user || !userData) {
        router.push('/admin');
        return;
      }

      if (!userData.isActive) {
        router.push('/admin');
        return;
      }

      // If permission-based check is specified, use that
      if (requiredPermission) {
        if (!hasRequiredPermission) {
          router.push('/you');
          return;
        }
        return; // Permission check passed, skip role check
      }

      // Check role hierarchy: owner > admin > editor > writer
      if (requiredRole === 'owner' && userData.role !== 'owner') {
        router.push('/you');
        return;
      }
      
      if (requiredRole === 'admin' && !['admin', 'owner'].includes(userData.role)) {
        router.push('/you');
        return;
      }
      
      if (requiredRole === 'editor' && !['editor', 'admin', 'owner'].includes(userData.role)) {
        router.push('/you');
        return;
      }

      // 'writer' is the lowest STAFF tier. 'contributor' is a user-world role BELOW writer
      // and must NEVER satisfy an /admin gate — explicitly exclude it (and any unknown role).
      if (requiredRole === 'writer' && !['writer', 'editor', 'admin', 'owner'].includes(userData.role)) {
        router.push('/admin');
        return;
      }
    }
  }, [user, userData, loading, requiredRole, requiredPermission, hasRequiredPermission, router]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !userData || !userData.isActive) {
    return null; // Will redirect in useEffect
  }

  // Permission-based check
  if (requiredPermission && !hasRequiredPermission) {
    return null; // Will redirect in useEffect
  }

  // Role-based check (only if permission not specified)
  if (!requiredPermission) {
    if (requiredRole === 'owner' && userData.role !== 'owner') {
      return null; // Will redirect in useEffect
    }
    
    if (requiredRole === 'admin' && !['admin', 'owner'].includes(userData.role)) {
      return null; // Will redirect in useEffect
    }
    
    if (requiredRole === 'editor' && !['editor', 'admin', 'owner'].includes(userData.role)) {
      return null; // Will redirect in useEffect
    }

    // 'writer' tier: staff-only allow-list (excludes contributor/member/unknown).
    if (requiredRole === 'writer' && !['writer', 'editor', 'admin', 'owner'].includes(userData.role)) {
      return null; // Will redirect in useEffect
    }
  }

  return <>{children}</>;
}
